import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { LogoMark } from '@caratuva/ui';
import { ApiError, requestMagicLink } from '../lib/api';
import { PRIVACY_URL, TERMS_URL } from '../lib/links';

interface LoginSearch {
  reason?: 'expired';
  redirect?: string;
  ref?: string;
}

// Passwordless seller sign-in. Submits an email to /v1/auth/signin which
// always returns {status:'sent'} so we render the same "check your email"
// success state regardless of whether the email is registered.
//
// Affiliate referral capture: a referral link looks like
// `/login?ref=<code>`. We validate the code, persist it to localStorage (it
// survives the magic-link round-trip — the user leaves for their inbox and
// comes back on a fresh navigation, sometimes without the query string), and
// send it along with the signin request so the API can attribute the new org
// to the referring affiliate. Absent a code, this is a total no-op.
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{4,32}$/;
const REFERRAL_CODE_STORAGE_KEY = 'caratuva.referralCode';

function readReferralCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('ref');
  if (raw && REFERRAL_CODE_PATTERN.test(raw)) return raw;
  return null;
}

function readStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
  if (raw && REFERRAL_CODE_PATTERN.test(raw)) return raw;
  return null;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    ...(search['reason'] === 'expired' ? { reason: 'expired' as const } : {}),
    ...(typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {}),
    ...(typeof search['ref'] === 'string' ? { ref: search['ref'] } : {}),
  }),
  component: LoginPage,
});

function LoginPage(): JSX.Element {
  const intl = useIntl();
  const { reason, ref } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fromUrl = readReferralCodeFromUrl();
    if (fromUrl && typeof window !== 'undefined') {
      window.localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, fromUrl);
    }
  }, []);

  const hasReferral = Boolean(ref && REFERRAL_CODE_PATTERN.test(ref));

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const referralCode = readReferralCodeFromUrl() ?? readStoredReferralCode() ?? undefined;
      await requestMagicLink(email.trim(), referralCode);
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : intl.formatMessage({
                id: 'auth.login.error.generic',
                defaultMessage: 'Sign-in failed. Please try again.',
              }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-lg border border-navy-200 bg-white p-8 shadow-sm">
        <header className="mb-6 flex flex-col items-center gap-3 text-navy-900">
          <LogoMark size={44} />
          <h1
            className="text-xl font-medium tracking-tight"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            {intl.formatMessage({
              id: 'auth.login.title',
              defaultMessage: 'Sign in to Caratuva',
            })}
          </h1>
          <p className="text-center text-sm text-navy-600">
            {intl.formatMessage({
              id: 'auth.login.subtitle',
              defaultMessage: 'Enter your email to sign in — or create a new account.',
            })}
          </p>
        </header>

        {hasReferral && !sent ? (
          <p className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-sm text-emerald-800">
            {intl.formatMessage({
              id: 'auth.login.referral.invited',
              defaultMessage: "You've been invited — 50% off our fee for your first month.",
            })}
          </p>
        ) : null}

        {reason === 'expired' && !sent ? (
          <p
            role="alert"
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            {intl.formatMessage({
              id: 'auth.session.expired',
              defaultMessage: 'Your session has expired. Please sign in again.',
            })}
          </p>
        ) : null}

        {sent ? (
          <div className="text-sm text-navy-700">
            <h2 className="mb-2 text-base font-medium text-navy-900">
              {intl.formatMessage({
                id: 'auth.login.sent.title',
                defaultMessage: 'Check your email',
              })}
            </h2>
            <p>
              {intl.formatMessage(
                {
                  id: 'auth.login.sent.body',
                  defaultMessage:
                    "We've sent a sign-in link to {email}. The link expires in 15 minutes.",
                },
                { email: email.trim() },
              )}
            </p>
          </div>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block text-navy-700">
                {intl.formatMessage({ id: 'auth.login.email', defaultMessage: 'Email' })}
              </span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-navy-200 px-3 py-2 text-navy-900 outline-none focus:border-navy-500"
              />
            </label>

            {error ? (
              <p className="text-sm text-[var(--color-danger)]" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting || email.trim().length === 0}
              className="w-full rounded-md bg-navy-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting
                ? intl.formatMessage({
                    id: 'auth.login.submitting',
                    defaultMessage: 'Sending link...',
                  })
                : intl.formatMessage({
                    id: 'auth.login.submit',
                    defaultMessage: 'Email me a sign-in link',
                  })}
            </button>

            <p className="text-center text-xs text-navy-500">
              <FormattedMessage
                id="auth.login.tos"
                defaultMessage="By continuing, you agree to our <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>."
                values={{
                  terms: (chunks) => (
                    <a
                      href={TERMS_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-navy-700"
                    >
                      {chunks}
                    </a>
                  ),
                  privacy: (chunks) => (
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-navy-700"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
